import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConversation } from '../../../contexts/ConversationContext';
import { useToast } from '../../../contexts/ToastContext';
import ChipManager, { ChatChip } from '../../work/ChipManager';
import {
  loadPersonaChips,
  savePersonaChips,
  PERSONA_CHIP_STORAGE_KEY,
} from '../../personas/PersonaChipDefaults';

import type { ComposeAction, ComposeTab } from './composeActions';
import { dispatchAction } from './composeActions';
import { JourneyTabs, JourneyTab } from './JourneyTabs';
import { ChipRow } from './ChipRow';
import { useLastActedTab } from './useLastActedTab';

import { probesTab } from './tabs/probesTab';
import { diagramsTab } from './tabs/diagramsTab';
import { conversationActionsTab } from './tabs/conversationActionsTab';
import { platformTab } from './tabs/platformTab';
import { buildChipsActions } from './tabs/chipsTab';
import { labTab, LAB_BENCHES } from './tabs/labTab';

import { diag, useDiagMount, useDiagWatch } from '../../../utils/trailhead_diag';

interface ComposePanelProps {
  personaId?: string;
  personaName?: string;
  sessionId?: string;
}

const CONSTRUCTION_STRIPES: React.CSSProperties = {
  backgroundImage: 'repeating-linear-gradient(45deg, #fbbf24 0 8px, #1f2937 8px 16px)',
};

const STALE_KEY_PREFIXES = [
  'compose.panel.expand.',
  'compose.panel.activeTab.',
  'compose.menuPin.',
];
const STALE_SINGLETON_KEYS = ['compose.panel.firstSeen'];

const DEFAULT_TAB_ID = 'chips';
const LAST_ACTED_KEY = (personaId?: string) =>
  `compose.panel.lastActed.${personaId || 'default'}`;

// Read directly from localStorage to avoid hook-state-timing issues
// when personaId changes (useLastActedTab's internal state hasn't
// flushed yet when ComposePanel's persona-switch effect fires).
function readLastActedDirect(personaId?: string): string | null {
  try {
    return localStorage.getItem(LAST_ACTED_KEY(personaId));
  } catch {
    return null;
  }
}

// Idempotent — safe to run twice in React 18 StrictMode dev.
function dropStaleKeys(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (STALE_KEY_PREFIXES.some((p) => key.startsWith(p))) {
        localStorage.removeItem(key);
      }
    }
    STALE_SINGLETON_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* localStorage unavailable */
  }
}

/**
 * ComposePanel — workshop journey surface (v0.5+, always-open).
 *
 * Always shows tabs + chip-row beneath. activeTabId initializes to
 * the persona's lastActedTabId (if any) else 'chips'. Tab clicks
 * swap the chip-row content instantly (no animation, no AnimatePresence
 * — operator field-test confirmed snappier is better). Chip clicks
 * dispatch + record-tab; the chip-row stays open ready for the next
 * action.
 *
 * Tailwind `dark:` variants throughout per
 * `feedback_dark_mode_parity_first_class`.
 */
export const ComposePanel: React.FC<ComposePanelProps> = ({
  personaId,
  personaName,
  sessionId,
}) => {
  const { sendMessage, requestInsert, state: convState } = useConversation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { lastActedTabId, recordTab } = useLastActedTab(personaId);

  // activeTabId is never null — workshop is always open. Initializer
  // reads lastActedTabId synchronously from the hook (which reads
  // localStorage on its first render).
  const [activeTabId, setActiveTabId] = useState<string>(
    () => lastActedTabId ?? DEFAULT_TAB_ID,
  );
  const [activeBenchId, setActiveBenchId] = useState<string>(LAB_BENCHES[0].id);
  const [chips, setChips] = useState<ChatChip[]>(() => loadPersonaChips());
  const [showChipManager, setShowChipManager] = useState<boolean>(false);

  useDiagMount('compose', 'ComposePanel', { personaId, sessionId });
  useDiagWatch('compose', 'state', {
    activeTabId,
    activeBenchId: activeTabId === 'lab' ? activeBenchId : null,
    lastActedTabId,
    chipCount: chips.length,
    showChipManager,
  });

  useEffect(() => {
    dropStaleKeys();
  }, []);

  // Persona switch — re-init activeTabId from the new persona's
  // lastActedTabId. Read directly from localStorage to avoid the
  // race where useLastActedTab's hook state hasn't flushed yet.
  const prevPersonaIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prevPersonaIdRef.current === personaId) return;
    prevPersonaIdRef.current = personaId;
    const nextLastActed = readLastActedDirect(personaId);
    setActiveTabId(nextLastActed ?? DEFAULT_TAB_ID);
    setActiveBenchId(LAB_BENCHES[0].id);
    setChips(loadPersonaChips());
  }, [personaId]);

  // Cross-tab chip sync.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === PERSONA_CHIP_STORAGE_KEY) setChips(loadPersonaChips());
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Defensive resync if activeBenchId drifts out of LAB_BENCHES.
  useEffect(() => {
    if (!LAB_BENCHES.find((b) => b.id === activeBenchId)) {
      diag({
        surface: 'compose',
        event: 'bench fallback · stale id',
        detail: { stale: activeBenchId, fallback: LAB_BENCHES[0].id },
      });
      setActiveBenchId(LAB_BENCHES[0].id);
    }
  }, [activeBenchId]);

  const dispatchCtx = useMemo(
    () => ({
      sendMessage,
      requestInsert,
      navigate: (route: string) => {
        const fullRoute = route === '/personas/forge' && personaId
          ? `${route}?personaId=${personaId}`
          : route;
        navigate(fullRoute);
      },
      exec: {
        sessionId,
        showToast,
      },
    }),
    [sendMessage, requestInsert, navigate, personaId, sessionId, showToast],
  );

  const onActionClick = useCallback(
    (action: ComposeAction) => {
      diag({
        surface: 'compose',
        event: 'dispatch · pill click',
        detail: {
          tabId: activeTabId,
          benchId: activeTabId === 'lab' ? activeBenchId : null,
          actionId: action.id,
          kind: action.kind,
        },
      });
      recordTab(activeTabId);
      dispatchAction(action, dispatchCtx);
    },
    [activeTabId, activeBenchId, dispatchCtx, recordTab],
  );

  const onTabClick = useCallback(
    (tabId: string) => {
      diag({
        surface: 'compose',
        event: 'tab click',
        detail: { tabId, prevActive: activeTabId },
      });
      setActiveTabId(tabId);
    },
    [activeTabId],
  );

  const onBenchClick = useCallback(
    (benchId: string) => {
      diag({
        surface: 'compose',
        event: 'bench click',
        detail: { benchId, prevBench: activeBenchId },
      });
      setActiveBenchId(benchId);
    },
    [activeBenchId],
  );

  const onChipsChange = useCallback((newChips: ChatChip[]) => {
    setChips(newChips);
    savePersonaChips(newChips);
  }, []);

  const tabs: ComposeTab[] = useMemo(() => [
    { id: 'chips', emoji: '⚡', label: 'Chips', descriptor: 'Persona one-tap chips', actions: buildChipsActions(chips) },
    probesTab,
    labTab,
    diagramsTab,
    conversationActionsTab,
    platformTab,
  ], [chips]);

  const journeyTabs: JourneyTab[] = useMemo(
    () => tabs.map((t) => ({ id: t.id, emoji: t.emoji, label: t.label })),
    [tabs],
  );

  const activeTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0];
  const labActiveBench = LAB_BENCHES.find((b) => b.id === activeBenchId) ?? LAB_BENCHES[0];
  const chipRowActions =
    activeTabId === 'lab' ? labActiveBench.actions : activeTab.actions;
  const isChips = activeTabId === 'chips';

  return (
    <div
      data-testid="compose-panel"
      className="shrink-0 min-w-0 bg-amber-50/30 dark:bg-gray-900/60"
    >
      <div
        className="h-2 w-full"
        style={CONSTRUCTION_STRIPES}
        aria-hidden
        data-testid="compose-panel-stripe-top"
      />
      <div className="flex items-center justify-between px-3 py-1.5 border-b bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-500/30 text-amber-900 dark:text-amber-200">
        <div className="flex items-center gap-1.5">
          <span aria-hidden className="text-base">🚧</span>
          <span className="text-xs font-bold tracking-widest uppercase">Compose Lab</span>
        </div>
        <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-amber-700/80 dark:text-amber-300/70">
          <span aria-hidden>⚠</span>
          <span>Experimental</span>
        </div>
      </div>

      <JourneyTabs
        tabs={journeyTabs}
        activeTabId={activeTabId}
        lastActedTabId={lastActedTabId}
        onTabClick={onTabClick}
      />

      <ChipRow
        actions={chipRowActions}
        lab={activeTabId === 'lab' ? {
          benches: LAB_BENCHES.map((b) => ({ id: b.id, label: b.label, actions: b.actions })),
          activeBenchId,
          onBenchClick,
        } : undefined}
        onActionClick={onActionClick}
        loading={convState.status === 'loading'}
        emptyState={isChips ? {
          message: 'No chips yet —',
          ctaLabel: '+ Add chip',
          onCta: () => setShowChipManager(true),
        } : undefined}
      />

      <div
        className="h-2 w-full"
        style={CONSTRUCTION_STRIPES}
        aria-hidden
        data-testid="compose-panel-stripe-bottom"
      />

      <ChipManager
        open={showChipManager}
        onClose={() => setShowChipManager(false)}
        chips={chips}
        onChipsChange={onChipsChange}
      />
    </div>
  );
};

export default ComposePanel;
